import { useState, useEffect } from 'react'
import { useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Cpu,
  Activity,
  Zap,
  ChevronLeft,
  ChevronRight,
  Users,
  CreditCard,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type FeatureRow = {
  feature: string
  inputTokens: number
  outputTokens: number
  cacheCreationTokens: number
  cacheReadTokens: number
  estimatedCostCents: number
  calls: number
}
type ModelRow = {
  model: string
  inputTokens: number
  outputTokens: number
  estimatedCostCents: number
  calls: number
}
type UserRow = {
  userId: string
  email: string
  name?: string
  estimatedCostCents: number
  calls: number
}
type PlanRow = {
  plan: string
  estimatedCostCents: number
  calls: number
  users: number
}
type DailyRow = {
  date: string
  estimatedCostCents: number
  totalTokens: number
  calls: number
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
  return count.toString()
}

const FEATURE_LABELS: Record<string, string> = {
  agent_chat: 'Agent Chat',
  suggested_actions: 'Suggested Actions',
  prompt_summary: 'Prompt Summary',
  frame_editing: 'Frame Editing',
  video_clip_analysis: 'Video Clip Analysis',
  video_section_analysis: 'Video Section Analysis',
  image_analysis: 'Image Analysis',
  video_frame_analysis: 'Video Frame Analysis',
  video_segment_aggregate: 'Video Segment Aggregate',
  full_video_analysis: 'Full Video Analysis',
  frame_analysis: 'Frame Analysis',
  multi_clip_analysis: 'Multi-Clip Analysis',
  session_frame_analysis: 'Session Frame Analysis',
  session_vision_aggregate: 'Session Vision Aggregate',
  admin_agent: 'Admin Agent',
  cli_vision_analysis: 'CLI Vision Analysis',
  cli_transcription: 'CLI Transcription',
  cli_multi_vision: 'CLI Multi-Vision Analysis',
}

function featureLabel(key: string): string {
  return FEATURE_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function modelShortName(model: string): string {
  if (model.includes('opus')) return 'Opus 4.5'
  if (model.includes('sonnet-4-5') || model.includes('sonnet-4-5')) return 'Sonnet 4.5'
  if (model.includes('sonnet-4-2')) return 'Sonnet 4'
  if (model.includes('haiku')) return 'Haiku 4.5'
  if (model.includes('gemini')) return 'Gemini 2.0 Flash'
  return model
}

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  active: 'Active',
  trialing: 'Trialing',
  canceled: 'Canceled',
  past_due: 'Past Due',
  unpaid: 'Unpaid',
  incomplete: 'Incomplete',
  incomplete_expired: 'Incomplete Expired',
  unknown: 'Unknown',
}

const PLAN_COLORS: Record<string, string> = {
  active: 'bg-green-500',
  trialing: 'bg-blue-500',
  free: 'bg-neutral-400',
  canceled: 'bg-red-400',
  past_due: 'bg-amber-500',
  unpaid: 'bg-red-500',
  incomplete: 'bg-orange-400',
  incomplete_expired: 'bg-neutral-300',
  unknown: 'bg-neutral-300',
}

type DashboardData = {
  month: string
  byFeature: FeatureRow[]
  byUser: UserRow[]
  byPlan: PlanRow[]
  byModel: ModelRow[]
  daily: DailyRow[]
  totals: {
    inputTokens: number
    outputTokens: number
    cacheCreationTokens: number
    cacheReadTokens: number
    estimatedCostCents: number
    calls: number
    cacheHitRate: number
  }
  byAttribution?: {
    user: { estimatedCostCents: number; calls: number; inputTokens: number; outputTokens: number }
    internal: { estimatedCostCents: number; calls: number; inputTokens: number; outputTokens: number }
  }
}

function todayString() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function shiftDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function UsageDashboard({ monthOffset, setMonthOffset }: {
  monthOffset: number
  setMonthOffset: (v: number) => void
}) {
  const getDashboard = useAction(api.llmUsage.getDashboard)
  const [data, setData] = useState<DashboardData | null>(null)
  const [userPage, setUserPage] = useState(0)
  const dayMode = true
  const [date, setDate] = useState(todayString)

  useEffect(() => {
    setData(null)
    setUserPage(0)
    const params = dayMode ? { date } : { monthOffset }
    getDashboard(params)
      .then((result) => setData(result as DashboardData))
      .catch((err) => console.error('[UsageDashboard] fetch failed:', err))
  }, [getDashboard, dayMode ? date : monthOffset])

  const USERS_PER_PAGE = 25
  const totalUsers = data?.byUser ? data.byUser.length : 0
  const totalUserPages = Math.ceil(totalUsers / USERS_PER_PAGE)
  const pagedUsers = data?.byUser
    ? data.byUser.slice(userPage * USERS_PER_PAGE, (userPage + 1) * USERS_PER_PAGE)
    : []

  const isToday = date === todayString()

  return (
    <div className="space-y-6">
      {/* Navigation */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => dayMode ? setDate(shiftDate(date, -1)) : setMonthOffset(monthOffset - 1)}
          className="p-1.5 rounded hover:bg-neutral-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xl font-bold text-blue-900 border border-blue-200 px-4 py-2 rounded-md bg-blue-50 min-w-[180px] text-center">
          {data ? data.month : '...'}
        </span>
        <button
          onClick={() => dayMode ? setDate(shiftDate(date, 1)) : setMonthOffset(Math.min(0, monthOffset + 1))}
          disabled={dayMode ? isToday : monthOffset >= 0}
          className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Estimated Cost</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {!data ? '...' : formatCurrency(data.totals.estimatedCostCents)}
                </p>
              </div>
              <Cpu className="h-5 w-5 text-purple-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Total Tokens</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {!data ? '...' : formatTokens(data.totals.inputTokens + data.totals.outputTokens)}
                </p>
              </div>
              <Activity className="h-5 w-5 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Cache Hit Rate</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {!data ? '...' : `${data.totals.cacheHitRate.toFixed(1)}%`}
                </p>
              </div>
              <Zap className="h-5 w-5 text-amber-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">API Calls</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {!data ? '...' : data.totals.calls.toLocaleString()}
                </p>
              </div>
              <Activity className="h-5 w-5 text-green-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Variable vs Fixed cost — user-attributable vs admin/internal tooling */}
      {data?.byAttribution && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-500">Variable cost (user-driven)</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(data.byAttribution.user.estimatedCostCents)}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {data.byAttribution.user.calls.toLocaleString()} calls
                    {data.totals.estimatedCostCents > 0 && (
                      <span> · {((data.byAttribution.user.estimatedCostCents / data.totals.estimatedCostCents) * 100).toFixed(0)}% of spend</span>
                    )}
                  </p>
                </div>
                <Activity className="h-5 w-5 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-500">Fixed cost (admin / internal)</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(data.byAttribution.internal.estimatedCostCents)}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {data.byAttribution.internal.calls.toLocaleString()} calls
                    {data.totals.estimatedCostCents > 0 && (
                      <span> · {((data.byAttribution.internal.estimatedCostCents / data.totals.estimatedCostCents) * 100).toFixed(0)}% of spend</span>
                    )}
                  </p>
                </div>
                <Cpu className="h-5 w-5 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Feature */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Cpu className="h-4 w-4" />
              Cost by Feature
            </CardTitle>
            <CardDescription>Estimated cost breakdown by feature</CardDescription>
          </CardHeader>
          <CardContent>
            {!data ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-300">Loading...</span>
                      <span className="text-sm text-neutral-300">--</span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full" />
                  </div>
                ))}
              </div>
            ) : data.byFeature.length === 0 ? (
              <p className="text-sm text-neutral-400 py-8 text-center">No usage data yet</p>
            ) : (
              <div className="space-y-4">
                {data.byFeature.map((item: FeatureRow) => (
                  <div key={item.feature}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-700">{featureLabel(item.feature)}</span>
                      <span className="text-sm font-medium text-neutral-900">
                        <span className="text-xs text-neutral-400 mr-1">({item.calls})</span>
                        {formatCurrency(item.estimatedCostCents)}
                      </span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-purple-500"
                        style={{
                          width: `${data.totals.estimatedCostCents > 0 ? Math.min((item.estimatedCostCents / data.totals.estimatedCostCents) * 100, 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {data && data.byFeature.length > 0 && (
              <div className="mt-6 pt-4 border-t border-neutral-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">Total</span>
                  <span className="text-sm font-bold text-neutral-900">
                    {formatCurrency(data.totals.estimatedCostCents)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost by Model */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Cost by Model
            </CardTitle>
            <CardDescription>Which models consume the most</CardDescription>
          </CardHeader>
          <CardContent>
            {!data ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-300">Loading...</span>
                      <span className="text-sm text-neutral-300">--</span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full" />
                  </div>
                ))}
              </div>
            ) : data.byModel.length === 0 ? (
              <p className="text-sm text-neutral-400 py-8 text-center">No usage data yet</p>
            ) : (
              <div className="space-y-4">
                {data.byModel.map((item: ModelRow) => (
                  <div key={item.model}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-700">{modelShortName(item.model)}</span>
                      <span className="text-sm font-medium text-neutral-900">
                        <span className="text-xs text-neutral-400 mr-1">({item.calls})</span>
                        {formatCurrency(item.estimatedCostCents)}
                      </span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-blue-500"
                        style={{
                          width: `${data.totals.estimatedCostCents > 0 ? Math.min((item.estimatedCostCents / data.totals.estimatedCostCents) * 100, 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by User */}
        {data && data.byUser && data.byUser.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Cost by User
              </CardTitle>
              <CardDescription>Top users by estimated cost ({totalUsers} users)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pagedUsers.map((item: UserRow) => (
                  <div key={item.userId}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-700">
                        {item.name ? `${item.name} (${item.email})` : item.email}
                      </span>
                      <span className="text-sm font-medium text-neutral-900">
                        <span className="text-xs text-neutral-400 mr-1">({item.calls})</span>
                        {formatCurrency(item.estimatedCostCents)}
                      </span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-green-500"
                        style={{
                          width: `${data.totals.estimatedCostCents > 0 ? Math.min((item.estimatedCostCents / data.totals.estimatedCostCents) * 100, 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {totalUserPages > 1 && (
                <div className="mt-4 pt-3 border-t border-neutral-200 flex items-center justify-between">
                  <span className="text-xs text-neutral-500">
                    {userPage * USERS_PER_PAGE + 1}–{Math.min((userPage + 1) * USERS_PER_PAGE, totalUsers)} of {totalUsers}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setUserPage(userPage - 1)}
                      disabled={userPage === 0}
                      className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-xs text-neutral-500 px-2">
                      {userPage + 1} / {totalUserPages}
                    </span>
                    <button
                      onClick={() => setUserPage(userPage + 1)}
                      disabled={userPage >= totalUserPages - 1}
                      className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cost by Plan */}
        {data && data.byPlan && data.byPlan.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Cost by Plan
              </CardTitle>
              <CardDescription>LLM cost breakdown by subscription status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {data.byPlan.map((item: PlanRow) => (
                  <div key={item.plan}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-700">
                        {PLAN_LABELS[item.plan] ?? item.plan}
                        <span className="text-xs text-neutral-400 ml-1">
                          ({item.users} {item.users === 1 ? 'user' : 'users'})
                        </span>
                      </span>
                      <span className="text-sm font-medium text-neutral-900">
                        <span className="text-xs text-neutral-400 mr-1">({item.calls})</span>
                        {formatCurrency(item.estimatedCostCents)}
                      </span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className={cn('h-full rounded-full', PLAN_COLORS[item.plan] ?? 'bg-neutral-400')}
                        style={{
                          width: `${data.totals.estimatedCostCents > 0 ? Math.min((item.estimatedCostCents / data.totals.estimatedCostCents) * 100, 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-neutral-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">Total</span>
                  <span className="text-sm font-bold text-neutral-900">
                    {formatCurrency(data.totals.estimatedCostCents)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Daily Trend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Daily Trend
          </CardTitle>
          <CardDescription>Cost and usage by day</CardDescription>
        </CardHeader>
        <CardContent>
          {!data ? (
            <div className="h-32 flex items-center justify-center text-neutral-300">Loading...</div>
          ) : data.daily.length === 0 ? (
            <p className="text-sm text-neutral-400 py-8 text-center">No usage data yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2 font-medium text-neutral-500">Date</th>
                    <th className="text-right py-2 font-medium text-neutral-500">Cost</th>
                    <th className="text-right py-2 font-medium text-neutral-500">Tokens</th>
                    <th className="text-right py-2 font-medium text-neutral-500">Calls</th>
                    <th className="py-2 pl-4 font-medium text-neutral-500 w-48"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((day: DailyRow) => {
                    const maxCost = Math.max(...data.daily.map((d: DailyRow) => d.estimatedCostCents))
                    return (
                      <tr key={day.date} className="border-b border-neutral-100">
                        <td className="py-2 text-neutral-700">
                          {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </td>
                        <td className="py-2 text-right font-medium text-neutral-900">
                          {formatCurrency(day.estimatedCostCents)}
                        </td>
                        <td className="py-2 text-right text-neutral-600">
                          {formatTokens(day.totalTokens)}
                        </td>
                        <td className="py-2 text-right text-neutral-600">
                          {day.calls}
                        </td>
                        <td className="py-2 pl-4">
                          <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                            <div
                              className={cn('h-full rounded-full bg-purple-400')}
                              style={{ width: `${maxCost > 0 ? (day.estimatedCostCents / maxCost) * 100 : 0}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
